import { useEffect, useState } from "react";

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "ico",
  "tiff",
  "tif",
  "avif",
]);

const DEFAULT_TIMEOUT_MS = 5000;

function getExtension(value: string): string {
  try {
    const pathname = new URL(value).pathname;
    const lastSegment = pathname.split("/").pop() ?? "";
    const parts = lastSegment.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  } catch {
    const clean = value?.split(/[?#]/)?.[0] ?? "";
    const parts = clean.split(".");
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
  }
}

function hasKnownImageExtension(url: string): boolean {
  return IMAGE_EXTENSIONS.has(getExtension(url));
}

export interface IWithUrl {
  url?: string;
}

/**
 * Probes whether each candidate's URL points to a valid image.
 * First checks known image extensions; falls back to loading each URL into a
 * hidden Image element with a configurable timeout.
 * Returns only the candidates whose URL resolves to a valid image.
 */
function probeUrl(
  url: string,
  onResolve: (valid: boolean) => void,
  timeoutMs: number,
): () => void {
  let settled = false;
  const img = new Image();

  const settle = (valid: boolean) => {
    if (settled) return;
    settled = true;
    clearTimeout(timeout);
    img.onload = null;
    img.onerror = null;
    img.src = "";
    onResolve(valid);
  };

  const timeout = setTimeout(() => settle(false), timeoutMs);

  img.onload = () => settle(true);
  img.onerror = () => settle(false);

  img.src = url;
  return () => {
    settled = true;
    clearTimeout(timeout);
    img.onload = null;
    img.onerror = null;
    img.src = "";
  };
}

function classifyCandidates<T extends IWithUrl>(candidates: T[]) {
  const immediatelyResolved: Record<string, boolean> = {};
  const urlsToProbe: string[] = [];
  candidates.forEach((c) => {
    if (!c.url) return;
    if (hasKnownImageExtension(c.url)) {
      immediatelyResolved[c.url] = true;
    } else {
      urlsToProbe.push(c.url);
    }
  });
  return { immediatelyResolved, urlsToProbe };
}

function useValidImages<T extends IWithUrl>(
  candidates: T[],
  { timeoutMs = DEFAULT_TIMEOUT_MS }: { timeoutMs?: number } = {},
): { validImages: T[]; isLoading: boolean } {
  const urlsKey = candidates.map((c) => c.url ?? "").join("|");

  // Classify synchronously so URLs with a recognisable extension are resolved
  // on the very first render, avoiding an unnecessary isLoading flash.
  const { immediatelyResolved, urlsToProbe } = classifyCandidates(candidates);

  const [resolvedMap, setResolvedMap] =
    useState<Record<string, boolean>>(immediatelyResolved);

  const makeOnResolve = (url: string) => (valid: boolean) =>
    setResolvedMap((prev) => ({ ...prev, [url]: valid }));

  useEffect(() => {
    // Reset to the fresh synchronous classification whenever candidates change,
    // but preserve previously-probed results for URLs still present.
    setResolvedMap((prev) => {
      const next: Record<string, boolean> = { ...immediatelyResolved };
      urlsToProbe.forEach((url) => {
        if (url in prev) {
          next[url] = prev[url];
        }
      });
      return next;
    });

    if (urlsToProbe.length === 0) return;

    const cleanups = urlsToProbe.map((url) =>
      probeUrl(url, makeOnResolve(url), timeoutMs),
    );

    return () => cleanups.forEach((fn) => fn());
  }, [urlsKey, timeoutMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const candidateUrls = candidates
    .map((c) => c.url)
    .filter((url): url is string => !!url);

  const isLoading = candidateUrls.some((url) => !(url in resolvedMap));
  const validImages = candidates.filter(
    (c) => c.url && resolvedMap[c.url] === true,
  );

  return { validImages, isLoading };
}

export default useValidImages;
